import { useContext, useEffect, useMemo, useState } from 'react';
import {
    Phone, Check, Clock, Search, Bell,
    X, Zap, Plus, CheckCircle2, Inbox, ChevronRight, XCircle, Eye,
} from 'lucide-react';
import Select from 'react-select';
import styles from './FollowupHome.module.css';
import { safeParseArray } from '../../Helper/helpers';
import { BASE_URL_API } from '../../References/urls';
import AuthContext from '../../store/auth-context';
import AddNewFollowup from '../../Components/Followup/AddNewFollowup';
import { executivesData, getExecutiveFullName } from '../../store/ExecutiveContext';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const SRC_META = {
    whatsapp: { label: 'WhatsApp', dot: 'sourceDotWhatsapp', badge: 'srcWhatsapp' },
    call: { label: 'Missed Call', dot: 'sourceDotCall', badge: 'srcCall' },
    application: { label: 'Applied', dot: 'sourceDotApplication', badge: 'srcApplication' },
    match: { label: 'AI Match', dot: 'sourceDotMatch', badge: 'srcMatch' },
    manual: { label: 'Manual', dot: 'sourceDotManual', badge: 'srcManual' },
};

const TIER_META = {
    overdue: { label: 'Overdue', dot: 'dotOverdue', labelCls: 'labelOverdue', timeCls: 'timeOverdue', cardCls: 'tierOverdue', focusBorder: 'focusCardBorderOverdue', focusTime: 'focusTimeOverdue', kpiCls: 'kpiOverdue' },
    today: { label: 'Today', dot: 'dotToday', labelCls: 'labelToday', timeCls: 'timeToday', cardCls: 'tierToday', focusBorder: 'focusCardBorderToday', focusTime: 'focusTimeToday', kpiCls: 'kpiToday' },
    tomorrow: { label: 'Tomorrow', dot: 'dotTomorrow', labelCls: 'labelTomorrow', timeCls: 'timeTomorrow', cardCls: 'tierTomorrow', focusBorder: 'focusCardBorderTomorrow', focusTime: 'focusTimeTomorrow', kpiCls: 'kpiTomorrow' },
    upcoming: { label: 'Upcoming', dot: 'dotUpcoming', labelCls: 'labelUpcoming', timeCls: 'timeUpcoming', cardCls: 'tierUpcoming', focusBorder: 'focusCardBorderUpcoming', focusTime: 'focusTimeUpcoming', kpiCls: 'kpiUpcoming' },
    done: { label: 'Done', dot: 'dotDone', labelCls: 'labelDone', timeCls: 'timeDone', cardCls: 'tierDone', focusBorder: '', focusTime: '', kpiCls: 'kpiDone' },
};

const PRI_DOT = { high: 'priHigh', medium: 'priMedium', low: 'priLow' };
const STAT_CLS = { overdue: 'kpiOverdue', today: 'kpiToday', tomorrow: 'kpiTomorrow', upcoming: 'kpiUpcoming', done: 'kpiDone' };
const ALL_RECRUITERS_OPTION_VALUE = '__all_recruiters__';
const OUTCOME_META = {
    connected:             { label: 'Connected',           color: '#15803d', bg: '#dcfce7' },
    not_reachable:         { label: 'Did not pick',        color: '#dc2626', bg: '#fef2f2' },
    busy:                  { label: 'Busy / Switched off', color: '#d97706', bg: '#fffbeb' },
    wrong_number:          { label: 'Wrong number',        color: '#dc2626', bg: '#fef2f2' },
    callback_requested:    { label: 'Callback requested',  color: '#0369a1', bg: '#eff6ff' },
    interested:            { label: 'Interested',          color: '#15803d', bg: '#dcfce7' },
    not_interested:        { label: 'Not interested',      color: '#64748b', bg: '#f1f5f9' },
    placed:                { label: 'Placed ✓',            color: '#15803d', bg: '#dcfce7' },
    dropped:               { label: 'Dropped',             color: '#64748b', bg: '#f1f5f9' },
    rescheduled_interview: { label: 'Interview scheduled', color: '#0369a1', bg: '#eff6ff' },
    interview_done:        { label: 'Interview done',      color: '#15803d', bg: '#dcfce7' },
    offer_given:           { label: 'Offer given',         color: '#0369a1', bg: '#eff6ff' },
    offer_accepted:        { label: 'Offer accepted',      color: '#15803d', bg: '#dcfce7' },
    offer_rejected:        { label: 'Offer rejected',      color: '#dc2626', bg: '#fef2f2' },
    no_show:               { label: 'No show',             color: '#dc2626', bg: '#fef2f2' },
};

const SNOOZE_TRIGGER_LABEL = {
    manual:     'Manual snooze',
    focus_mode: 'Snoozed in Focus Mode',
    bulk:       'Bulk action',
};

const CANCEL_REASONS = [
    { value: 'requirement_withdrawn', label: 'Requirement Withdrawn' },
    { value: 'placed_elsewhere', label: 'Placed Elsewhere' },
    { value: 'candidate_not_interested', label: 'Candidate Not Interested' },
    { value: 'candidate_not_reachable', label: 'Candidate Not Reachable' },
    { value: 'duplicate_followup', label: 'Duplicate Follow-up' },
    { value: 'candidate_relocated', label: 'Candidate Relocated' },
    { value: 'salon_closed', label: 'Salon Closed' },
    { value: 'wrong_candidate', label: 'Wrong Candidate' },
    { value: 'candidate_blacklisted', label: 'Candidate Blacklisted' },
    { value: 'other', label: 'Other' },
];

const NO_CONVERSATION_REASON_OPTIONS = [
    { value: 'busy_another_call', label: 'Busy on Another Call' },
    { value: 'call_disconnected', label: 'Call disconnected' },
    { value: 'call_dropped', label: 'Call dropped in between' },
    { value: 'candidate_busy', label: 'Candidate is Busy' },
    { value: 'did_not_pick', label: 'Did not pick the call' },
    { value: 'hanging_up', label: 'Hanging Up the call' },
    { value: 'invalid_number', label: 'Invalid Number' },
    { value: 'no_incoming', label: 'No Incoming Available' },
    { value: 'out_of_reach', label: 'Out of Reach' },
    { value: 'someone_else_picked', label: 'Someone else picked' },
    { value: 'switched_off', label: 'Switch Off' },
    { value: 'wrong_number', label: 'Wrong number' },
    { value: 'wrong_person', label: 'Wrong Person Number' },
];
const EXECUTIVE_SELECT_STYLES = {
    control: (base, state) => ({
        ...base,
        minHeight: 34,
        borderRadius: 8,
        borderColor: state.isFocused ? '#38bdf8' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(14, 165, 233, 0.15)' : 'none',
        backgroundColor: '#f8fafc',
        paddingLeft: 2,
        ':hover': {
            borderColor: '#38bdf8',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '2px 6px',
        gap: 4,
    }),
    placeholder: (base) => ({
        ...base,
        color: '#94a3b8',
        fontSize: 11,
    }),
    input: (base) => ({
        ...base,
        color: '#0f172a',
        fontSize: 11,
    }),
    singleValue: (base) => ({
        ...base,
        color: '#0f172a',
        fontSize: 11,
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#dbeafe',
        borderRadius: 999,
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#0369a1',
        fontSize: 10,
        fontWeight: 700,
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#0369a1',
        borderRadius: 999,
        ':hover': {
            backgroundColor: '#bfdbfe',
            color: '#075985',
        },
    }),
    menu: (base) => ({
        ...base,
        zIndex: 30,
        borderRadius: 10,
        overflow: 'hidden',
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 11,
        backgroundColor: state.isSelected ? '#0369a1' : state.isFocused ? '#eff6ff' : '#fff',
        color: state.isSelected ? '#fff' : '#0f172a',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#94a3b8',
        padding: 6,
    }),
    clearIndicator: (base) => ({
        ...base,
        color: '#94a3b8',
        padding: 6,
    }),
};

// ─── UTILS ───────────────────────────────────────────────────────────────────
const getTier = (f) => {
    if (f.status === 'done' || f.status === 'cancelled') return 'done';
    const currentTime = new Date();
    const scheduledTime = f?.scheduledAt instanceof Date ? f.scheduledAt : new Date(f?.scheduledAt);
    if (Number.isNaN(scheduledTime.getTime())) return 'upcoming';

    if (scheduledTime < currentTime) return 'overdue';

    const startOfToday = new Date(currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate());
    const startOfTomorrow = new Date(startOfToday);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
    const startOfDayAfterTomorrow = new Date(startOfTomorrow);
    startOfDayAfterTomorrow.setDate(startOfDayAfterTomorrow.getDate() + 1);

    if (scheduledTime < startOfTomorrow) return 'today';
    if (scheduledTime < startOfDayAfterTomorrow) return 'tomorrow';
    return 'upcoming';
};

const timeLabel = (d) => {
    const currentTime = new Date();
    const targetTime = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(targetTime.getTime())) return '—';

    const h = (targetTime - currentTime) / 3600000;
    if (h < -24) return `${Math.floor(-h / 24)}d overdue`;
    if (h < 0) return Math.abs(h) < 1 ? `${Math.round(-h * 60)}m ago` : `${Math.round(-h)}h ago`;
    if (h < 1) return `in ${Math.round(h * 60)}m`;
    const startOfToday = new Date(currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate());
    const startOfTomorrow = new Date(startOfToday);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
    const startOfDayAfterTomorrow = new Date(startOfTomorrow);
    startOfDayAfterTomorrow.setDate(startOfDayAfterTomorrow.getDate() + 1);

    if (targetTime < startOfTomorrow) return `in ${Math.round(h)}h`;
    if (targetTime < startOfDayAfterTomorrow) return 'Tomorrow';
    return `in ${Math.ceil(h / 24)}d`;
};

const urgencyScore = (f) => {
    const ts = { overdue: 100, today: 60, tomorrow: 30, upcoming: 10, done: 0 };
    const ps = { high: 50, medium: 25, low: 10 };
    return (ts[getTier(f)] || 0) + ps[f.priority] + f.score * 0.4;
};

const formatDateForApi = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateTimeForApi = (value) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return null;
    const pad = (item) => String(item).padStart(2, '0');
    return `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())} ${pad(parsedDate.getHours())}:${pad(parsedDate.getMinutes())}:00`;
};

const parseDateValue = (value) => {
    if (!value) return new Date();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const formatMetaDateTime = (value) => {
    if (!value) return '—';
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

const normalizePriority = (priorityValue) => {
    const normalized = String(priorityValue || '').toLowerCase();
    if (['high', 'critical', 'urgent'].includes(normalized)) return 'high';
    if (['medium', 'mid'].includes(normalized)) return 'medium';
    return 'low';
};

const normalizeStatus = (statusValue, isResolved) => {
    const normalized = String(statusValue || '').toLowerCase();
    if (['done', 'resolved', 'completed', 'closed'].includes(normalized) || String(isResolved) === '1') {
        return 'done';
    }
    if (normalized === 'cancelled' || normalized === 'canceled') return 'cancelled';
    if (normalized === 'in_progress' || normalized === 'inprogress') return 'in_progress';
    return 'pending';
};

const normalizeSource = (sourceValue) => {
    const normalized = String(sourceValue || '').toLowerCase();
    if (normalized.includes('whatsapp') || normalized === 'wa') return 'whatsapp';
    if (normalized.includes('call')) return 'call';
    if (normalized.includes('application') || normalized.includes('applied')) return 'application';
    if (normalized.includes('match')) return 'match';
    return 'manual';
};

const normalizeTags = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map((item) => String(item).toLowerCase());
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map((item) => String(item).toLowerCase());
    } catch (error) {
    }
    return String(value).split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
};

const mapApiFollowupToUi = (item, index) => {
    const source = normalizeSource(item?.followup_type || item?.source || item?.sourceType || item?.followupSource);
    const status = normalizeStatus(item?.status || item?.followupStatus, item?.isResolved);
    const recruiterIdentifier = item?.executive_identifier || item?.executiveIdentifier || item?.actionBy || item?.recruiterIdentifier || item?.recruiter_id || '';

    return {
        id: item?.id || item?.ID || item?.followupId || item?.caseIdentifier || Date.now() + index,
        followup_uuid: item?.followup_uuid || item?.followupUuid || null,
        created_at: item?.created_at || item?.createdAt || null,
        name: item?.candidateName || item?.stylist_name || item?.name || 'Unknown Candidate',
        phone: item?.contact_number || 'missed phone number',
        role: item?.role || item?.workRole || item?.primaryWorkDomain || 'Stylist',
        city: item?.city || item?.currentLocation || 'NA',
        score: Number(item?.urgency_score || item?.score || item?.matchingScore || item?.fitScore || 60),
        source,
        sourceLabel: item?.source_label || item?.sourceLabel || item?.remarks || item?.subject || SRC_META[source]?.label || 'Follow-up',
        note: item?.note || item?.remarks || item?.subject || 'No note available',
        scheduledAt: parseDateValue(item?.scheduled_at || item?.scheduledAt || item?.followupOn || item?.nextFollowupOn || item?.addedOn),
        priority: normalizePriority(item?.priority || item?.priorityLabel),
        status,
        recruiterIdentifier,
        recruiter: item?.recruiterName || item?.executiveName || item?.actionByName || item?.actionBy || 'Recruiter',
        salon: item?.salon_name || item?.salonName || item?.salon || item?.companyName || item?.brandName || '—',
        type: source === 'whatsapp' ? 'whatsapp' : 'call',
        snooze_count: Number(item?.snooze_count || item?.snoozeCount || 0),
        tags: normalizeTags(item?.tags || item?.labels),
    };
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function FollowupHome() {
    const authCtx = useContext(AuthContext);
    const todayDate = formatDateForApi(new Date());
    const hasAccessTo = safeParseArray(authCtx?.hasAccessTo);
    const [data, setData] = useState([]);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [tab, setTab] = useState('all');
    const [q, setQ] = useState('');
    const [fSrc, setFSrc] = useState('all');
    const [fPri, setFPri] = useState('all');
    const [selectedRecruiters, setSelectedRecruiters] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [snoozeId, setSnoozeId] = useState(null);
    const [isSnoozing, setIsSnoozing] = useState(false);
    const [snoozingFollowupId, setSnoozingFollowupId] = useState(null);
    const [snoozeError, setSnoozeError] = useState('');
    const [outcomeSheetState, setOutcomeSheetState] = useState(null);
    const [isUpdatingOutcome, setIsUpdatingOutcome] = useState(false);
    const [outcomeError, setOutcomeError] = useState('');
    const [focusMode, setFocusMode] = useState(false);
    const [focusIdx, setFocusIdx] = useState(0);
    const [addOpen, setAddOpen] = useState(false);
    const [isAddNewFollowupOpen, setIsAddNewFollowupOpen] = useState(false)
    const [newNote, setNewNote] = useState({ name: '', role: '', city: '', salon: '', note: '', hours: '1' });
    const selectedRecruiterIds = useMemo(
        () => selectedRecruiters
            .map((item) => item?.value)
            .filter((value) => Boolean(value) && value !== ALL_RECRUITERS_OPTION_VALUE),
        [selectedRecruiters]
    );
    const selectedRecruiterKey = selectedRecruiterIds.join(',');
    const [activeStylistIdentifier, setActiveStylistIdentifier] = useState('M11001684061586866274')
    const [cancelModalState, setCancelModalState] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelError, setCancelError] = useState('');
    const [toast, setToast] = useState(null);
    const [detailDrawerId, setDetailDrawerId] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [isFetchingDetail, setIsFetchingDetail] = useState(false);
    const [detailError, setDetailError] = useState('');

    const getRecruiterDailyFollowup = async () => {
        if (!authCtx?.token) {
            return;
        }

        setIsFetchingData(true);
        try {
            const response = await fetch(BASE_URL_API + '/getRecruiterDailyFollowup', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    executiveIdentifier: authCtx.userIdentifier,
                    executiveIdentifiers: selectedRecruiterIds.length > 0 ? selectedRecruiterIds : [authCtx.userIdentifier],
                    startDate: todayDate,
                    endDate: todayDate,
                    categoryIdentifiers: [],
                })
            });

            if (!response.ok) {
                console.log('Something went wrong : Server Error');
                setData((prev) => (prev.length > 0 ? prev : []));
                return;
            }

            const responseBody = await response.json();
            if (responseBody?.success === true || responseBody?.status === 'success') {
                const followupsArray = responseBody?.followups || responseBody?.response || [];
                const normalizedData = followupsArray.map((item, index) => mapApiFollowupToUi(item, index));
                setData(normalizedData);
            } else {
                if (responseBody?.message === 'token Expired, please login again!') {
                    authCtx.logout();
                } else {
                    setData([]);
                }
            }
        } catch (error) {
            console.error('getRecruiterDailyFollowup error:', error);
            setData((prev) => (prev.length > 0 ? prev : []));
        } finally {
            setIsFetchingData(false);
        }
    };

    useEffect(() => {
        getRecruiterDailyFollowup();
    }, [authCtx?.token, authCtx?.userIdentifier, selectedRecruiterKey]);

    // ── derived counts ────────────────────────────────────────
    const isActive = (f) => f.status === 'pending' || f.status === 'in_progress';

    const counts = useMemo(() => ({
        all: data.filter(isActive).length,
        overdue: data.filter(f => getTier(f) === 'overdue').length,
        today: data.filter(f => getTier(f) === 'today').length,
        tomorrow: data.filter(f => getTier(f) === 'tomorrow').length,
        upcoming: data.filter(f => getTier(f) === 'upcoming').length,
        done: data.filter(f => f.status === 'done').length,
    }), [data]);

    const srcCounts = useMemo(() => {
        const c = {};
        data.filter(isActive).forEach(f => { c[f.source] = (c[f.source] || 0) + 1; });
        return c;
    }, [data]);

    const priCounts = useMemo(() => {
        const c = { high: 0, medium: 0, low: 0 };
        data.filter(isActive).forEach(f => { c[f.priority]++; });
        return c;
    }, [data]);

    const filtered = useMemo(() => {
        let d = data.filter(f => {
            if (tab === 'done') return f.status === 'done';
            if (tab === 'all') return isActive(f);
            return getTier(f) === tab && isActive(f);
        });
        if (q) {
            const lq = q.toLowerCase();
            d = d.filter(f => [f.name, f.role, f.city, f.salon, f.note].some(v => String(v || '').toLowerCase().includes(lq)));
        }
        if (fSrc !== 'all') d = d.filter(f => f.source === fSrc);
        if (fPri !== 'all') d = d.filter(f => f.priority === fPri);
        return [...d].sort((a, b) => urgencyScore(b) - urgencyScore(a));
    }, [data, tab, q, fSrc, fPri]);

    const grouped = useMemo(() => {
        if (tab !== 'all') return null;
        const g = { overdue: [], today: [], tomorrow: [], upcoming: [] };
        filtered.forEach(f => { const tier = getTier(f); if (g[tier]) g[tier].push(f); });
        return g;
    }, [filtered, tab]);

    const focusQueue = useMemo(() =>
        data.filter(f => ['overdue', 'today'].includes(getTier(f)) && isActive(f))
            .sort((a, b) => urgencyScore(b) - urgencyScore(a))
        , [data]);
    const isInitialDataLoading = isFetchingData && data.length === 0;

    const recruiterOptions = useMemo(() => {
        const optionsExecutive =
            authCtx?.userIdentifier === '999964N8406'
                ? executivesData?.map((executive) => ({
                    value: executive?.userID,
                    label: getExecutiveFullName(executive?.userID) || executive?.userID,
                })) ?? []
                : [
                    ...(authCtx?.userIdentifier
                        ? [{
                            value: authCtx.userIdentifier,
                            label: `${getExecutiveFullName(authCtx.userIdentifier) || authCtx.userIdentifier} (me)`,
                        }]
                        : []),
                    ...hasAccessTo.map((executiveIdentifier) => ({
                        value: executiveIdentifier,
                        label: getExecutiveFullName(executiveIdentifier) || executiveIdentifier,
                    })),
                ];

        const uniqueOptions = optionsExecutive.filter((option, index, list) => {
            return option?.value && list.findIndex((item) => item.value === option.value) === index;
        });

        return uniqueOptions;
    }, [authCtx?.userIdentifier, hasAccessTo]);

    const recruiterSelectOptions = useMemo(() => {
        if (!recruiterOptions.length) return recruiterOptions;
        const allSelected = selectedRecruiters.length === recruiterOptions.length;
        return [
            {
                value: ALL_RECRUITERS_OPTION_VALUE,
                label: allSelected ? 'Deselect all recruiters' : 'Select all recruiters',
            },
            ...recruiterOptions,
        ];
    }, [recruiterOptions, selectedRecruiters]);

    // ── actions ───────────────────────────────────────────────
    const markDone = (id, trigger = 'done', advanceFocus = false) => {
        setOutcomeError('');
        setOutcomeSheetState({ id, trigger, advanceFocus });
    };

    const updateFollowupOutcome = async (payload) => {
        const followupId = payload?.id;
        const targetFollowup = data.find((item) => item.id === followupId);

        if (!targetFollowup) {
            setOutcomeError('Follow-up not found. Please refresh and try again.');
            return false;
        }
        if (!targetFollowup?.followup_uuid) {
            setOutcomeError('This follow-up cannot be updated because UUID is missing.');
            return false;
        }
        if (!authCtx?.token || !authCtx?.userIdentifier) {
            setOutcomeError('Session expired. Please login again.');
            return false;
        }

        const apiPayload = {
            token: authCtx.token,
            followup_uuid: targetFollowup.followup_uuid,
            actioned_by: authCtx.userIdentifier,
            action: payload.action,
            outcome_note: String(payload.outcomeNote || '').trim(),
        };

        if (payload.action === 'had_conversation') {
            apiPayload.sub_action = payload.subAction;
            if (payload.subAction === 'interview_scheduled') {
                const interviewDate = formatDateTimeForApi(payload.interviewDate);
                if (!interviewDate) {
                    setOutcomeError('Please select a valid interview date & time.');
                    return false;
                }
                apiPayload.interview_date = interviewDate;
            }
            if (payload.subAction === 'snooze') {
                const snoozeHours = Number(payload.snoozeHours);
                if (Number.isNaN(snoozeHours) || snoozeHours <= 0) {
                    setOutcomeError('Please select a valid snooze duration.');
                    return false;
                }
                apiPayload.snooze_hours = snoozeHours;
                apiPayload.snooze_reason = String(payload.snoozeReason || '').trim() || `Callback requested after ${snoozeHours} hour(s)`;
            }
        }

        if (payload.action === 'no_conversation') {
            if (!payload.noConversationReason) {
                setOutcomeError('Please select a no conversation reason.');
                return false;
            }
            apiPayload.no_conversation_reason = payload.noConversationReason;
        }

        setIsUpdatingOutcome(true);
        setOutcomeError('');

        try {
            const response = await fetch(BASE_URL_API + '/updateFollowupStatus', {
                method: 'POST',
                body: JSON.stringify(apiPayload),
            });

            if (!response.ok) {
                setOutcomeError('Server error while updating follow-up. Please try again.');
                return false;
            }

            const responseBody = await response.json();
            const isRequestSuccessful = responseBody?.success === true || responseBody?.status === 'success';

            if (!isRequestSuccessful) {
                if (responseBody?.message === 'token Expired, please login again!') {
                    authCtx.logout();
                    return false;
                }
                setOutcomeError(responseBody?.message || 'Failed to update follow-up status.');
                return false;
            }

            const updatedUuid = responseBody?.followup_uuid || targetFollowup.followup_uuid;
            const mappedStatus = String(responseBody?.new_status || '').toLowerCase();
            const nextStatus = mappedStatus === 'done' ? 'done' : 'pending';
            const updatedSnoozeCount = Number(responseBody?.snooze_count);
            const rescheduledAt = responseBody?.rescheduled_at ? parseDateValue(responseBody.rescheduled_at) : null;

            setData((prev) => prev.map((item) => {
                const isTarget = item.id === followupId || item.followup_uuid === updatedUuid;
                if (!isTarget) return item;

                return {
                    ...item,
                    followup_uuid: updatedUuid,
                    status: nextStatus,
                    scheduledAt: rescheduledAt || item.scheduledAt,
                    snooze_count: Number.isNaN(updatedSnoozeCount) ? (item.snooze_count || 0) : updatedSnoozeCount,
                };
            }));

            const shouldAdvanceFocus = outcomeSheetState?.advanceFocus;
            setOutcomeSheetState(null);
            setOutcomeError('');

            if (shouldAdvanceFocus) {
                setFocusIdx((index) => Math.min(index, Math.max(0, focusQueue.length - 2)));
            }

            return true;
        } catch (error) {
            console.error('updateFollowupStatus error:', error);
            setOutcomeError('Network error while updating follow-up. Please check your connection.');
            return false;
        } finally {
            setIsUpdatingOutcome(false);
        }
    };
    const doSnooze = async (id, h, reason = '') => {
        const snoozeHours = Number(h);
        if (!id || Number.isNaN(snoozeHours) || snoozeHours <= 0) {
            setSnoozeError('Invalid snooze duration selected.');
            return false;
        }

        const targetFollowup = data.find((item) => item.id === id);
        if (!targetFollowup) {
            setSnoozeError('Follow-up not found. Please refresh and try again.');
            return false;
        }

        if (!authCtx?.token || !authCtx?.userIdentifier) {
            setSnoozeError('Session expired. Please login again.');
            return false;
        }

        if (!targetFollowup?.followup_uuid) {
            setSnoozeError('This follow-up cannot be snoozed because UUID is missing.');
            return false;
        }

        setSnoozeError('');
        setIsSnoozing(true);
        setSnoozingFollowupId(id);

        try {
            const response = await fetch(BASE_URL_API + '/snoozeDailyFollowup', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    followup_uuid: targetFollowup.followup_uuid,
                    snoozed_by: authCtx.userIdentifier,
                    snooze_hours: snoozeHours,
                    snooze_reason: String(reason || '').trim() || `Snoozed for ${snoozeHours} hour(s)`,
                    snooze_trigger: 'manual',
                }),
            });

            if (!response.ok) {
                setSnoozeError('Server error while snoozing. Please try again.');
                return false;
            }

            const responseBody = await response.json();
            const isRequestSuccessful = responseBody?.success === true || responseBody?.status === 'success';

            if (!isRequestSuccessful) {
                if (responseBody?.message === 'token Expired, please login again!') {
                    authCtx.logout();
                    return false;
                }
                setSnoozeError(responseBody?.message || 'Failed to snooze follow-up. Please try again.');
                return false;
            }

            const snoozedToDate = parseDateValue(responseBody?.snoozed_to || responseBody?.snoozedTo);
            const updatedScoreRaw = responseBody?.urgency_score;
            const updatedScore = Number(updatedScoreRaw);
            const updatedSnoozeCount = Number(responseBody?.snooze_count);
            const updatedUuid = responseBody?.followup_uuid || targetFollowup.followup_uuid;

            setData((prev) => prev.map((item) => {
                const isTarget = item.id === id || item.followup_uuid === updatedUuid;
                if (!isTarget) return item;

                return {
                    ...item,
                    followup_uuid: updatedUuid,
                    scheduledAt: snoozedToDate,
                    status: 'pending',
                    score: Number.isNaN(updatedScore) ? item.score : updatedScore,
                    snooze_count: Number.isNaN(updatedSnoozeCount) ? (item.snooze_count || 0) : updatedSnoozeCount,
                };
            }));

            setSnoozeId(null);
            setSnoozeError('');
            return true;
        } catch (error) {
            console.error('Snooze follow-up error:', error);
            setSnoozeError('Network error while snoozing. Please check your connection.');
            return false;
        } finally {
            setIsSnoozing(false);
            setSnoozingFollowupId(null);
        }
    };
    const toggleSel = (id) => { setSelected(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; }); };
    const bulkDone = () => { setData(p => p.map(f => selected.has(f.id) ? { ...f, status: 'done' } : f)); setSelected(new Set()); };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const cancelFollowup = async (payload) => {
        const followupId = cancelModalState?.id;
        const targetFollowup = data.find((item) => item.id === followupId);

        if (!targetFollowup) {
            setCancelError('Follow-up not found. Please refresh and try again.');
            return false;
        }
        if (!targetFollowup?.followup_uuid) {
            setCancelError('This follow-up cannot be cancelled because UUID is missing.');
            return false;
        }
        if (!authCtx?.token || !authCtx?.userIdentifier) {
            setCancelError('Session expired. Please login again.');
            return false;
        }

        const originalStatus = targetFollowup.status;

        // optimistic remove
        setData((prev) => prev.map((item) =>
            item.id === followupId ? { ...item, status: 'cancelled' } : item
        ));
        setCancelModalState(null);
        setCancelError('');

        setIsCancelling(true);

        try {
            const response = await fetch(BASE_URL_API + '/cancelFollowup', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    followup_uuid: targetFollowup.followup_uuid,
                    cancelled_by: authCtx.userIdentifier,
                    cancel_reason: payload.cancelReason,
                    cancel_note: payload.cancelNote || '',
                }),
            });

            if (!response.ok) {
                setData((prev) => prev.map((item) =>
                    item.id === followupId ? { ...item, status: originalStatus } : item
                ));
                showToast('Server error while cancelling. Please try again.', 'error');
                return false;
            }

            const responseBody = await response.json();
            const isSuccess = responseBody?.success === true;

            if (!isSuccess) {
                if (responseBody?.message === 'token Expired, please login again!') {
                    authCtx.logout();
                    return false;
                }

                const alreadyTerminal =
                    responseBody?.message === 'Follow-up is already cancelled' ||
                    responseBody?.message === 'Cannot cancel a follow-up that is already done';

                if (!alreadyTerminal) {
                    setData((prev) => prev.map((item) =>
                        item.id === followupId ? { ...item, status: originalStatus } : item
                    ));
                }

                showToast(responseBody?.message || 'Failed to cancel follow-up.', 'error');
                return false;
            }

            const updatedUuid = responseBody?.followup_uuid || targetFollowup.followup_uuid;
            setData((prev) => prev.map((item) => {
                const isTarget = item.id === followupId || item.followup_uuid === updatedUuid;
                if (!isTarget) return item;
                return { ...item, followup_uuid: updatedUuid, status: 'cancelled' };
            }));

            showToast('Follow-up cancelled');
            return true;
        } catch (error) {
            console.error('cancelFollowup error:', error);
            setData((prev) => prev.map((item) =>
                item.id === followupId ? { ...item, status: originalStatus } : item
            ));
            showToast('Network error while cancelling. Please check your connection.', 'error');
            return false;
        } finally {
            setIsCancelling(false);
        }
    };

    const openDetail = async (followupId) => {
        const targetFollowup = data.find((item) => item.id === followupId);
        setDetailDrawerId(followupId);
        setDetailData(null);
        setDetailError('');
        setIsFetchingDetail(true);

        if (!targetFollowup?.followup_uuid) {
            setDetailError('Cannot load details: follow-up UUID is missing.');
            setIsFetchingDetail(false);
            return;
        }

        try {
            const response = await fetch(BASE_URL_API + '/getFollowupDetail', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    followup_uuid: targetFollowup.followup_uuid,
                }),
            });

            if (!response.ok) {
                setDetailError('Server error. Please try again.');
                return;
            }

            const responseBody = await response.json();
            if (responseBody?.success === true) {
                setDetailData(responseBody);
            } else {
                if (responseBody?.message === 'token Expired, please login again!') {
                    authCtx.logout();
                    return;
                }
                setDetailError(responseBody?.message || 'Failed to load follow-up details.');
            }
        } catch (error) {
            console.error('getFollowupDetail error:', error);
            setDetailError('Network error. Please check your connection.');
        } finally {
            setIsFetchingDetail(false);
        }
    };

    const closeDetail = () => {
        setDetailDrawerId(null);
        setDetailData(null);
        setDetailError('');
    };

    const retryDetail = () => {
        if (detailDrawerId !== null) openDetail(detailDrawerId);
    };

    const addFollowup = (newFollowup) => {
        // newFollowup comes from AddNewFollowup after successful API save
        if (!newFollowup) return;
        const fallbackHours = Number(newFollowup.hours);
        const fallbackScheduledDate = new Date(
            Date.now() + (Number.isNaN(fallbackHours) ? 1 : fallbackHours) * 3600000
        );
        const scheduledDate = newFollowup.scheduledAt
            ? (typeof newFollowup.scheduledAt === 'string'
                ? new Date(newFollowup.scheduledAt.replace(' ', 'T'))
                : newFollowup.scheduledAt)
            : fallbackScheduledDate;
        
        const followupType = newFollowup.channel || newFollowup.followup_type || 'call';
        const normalizedSource = normalizeSource(followupType);
        
        setData(p => [...p, {
            id: newFollowup.id || Date.now(),
            followup_uuid: newFollowup.followupUuid || newFollowup.followup_uuid || null,
            created_at: new Date().toISOString(),
            name: newFollowup.name || '',
            phone: newFollowup.phone || newFollowup.stylist_identifier || '',
            role: newFollowup.role || 'Stylist',
            city: newFollowup.city || 'Delhi',
            score: Number(newFollowup.urgencyScore || newFollowup.urgency_score || newFollowup.score || newFollowup.ai_candidate_score || 60),
            source: newFollowup.source || normalizedSource,
            sourceLabel: newFollowup.sourceLabel || newFollowup.source_label || 'Manual entry',
            note: newFollowup.note || '',
            scheduledAt: scheduledDate,
            priority: newFollowup.priority || 'medium',
            status: 'pending',
            recruiterIdentifier: newFollowup.executiveIdentifier || authCtx?.userIdentifier || '',
            recruiter: newFollowup.recruiter || authCtx?.userIdentifier || 'Recruiter',
            salon: newFollowup.salon || newFollowup.salon_name || '—',
            type: normalizedSource === 'whatsapp' ? 'whatsapp' : 'call',
            snooze_count: Number(newFollowup.snooze_count || 0),
            tags: newFollowup.tags || [],
        }]);
        setNewNote({ name: '', role: '', city: '', salon: '', note: '', hours: '1' });
        setAddOpen(false);
        setIsAddNewFollowupOpen(false);
    };

    const completionRate = counts.done > 0 ? Math.round((counts.done / (counts.done + counts.all)) * 100) : 0;
    const priTotal = counts.all || 1;
    const activeFilterChips = [
        fSrc !== 'all' && { key: 'src', label: SRC_META[fSrc]?.label, clear: () => setFSrc('all') },
        fPri !== 'all' && { key: 'pri', label: fPri.charAt(0).toUpperCase() + fPri.slice(1) + ' priority', clear: () => setFPri('all') },
        selectedRecruiterIds.length > 0 && {
            key: 'rec',
            label: `${selectedRecruiterIds.length} recruiter${selectedRecruiterIds.length > 1 ? 's' : ''}`,
            clear: () => setSelectedRecruiters([]),
        },
    ].filter(Boolean);

    // ══════════════════════════════════════════════════════════
    //  FOCUS MODE
    // ══════════════════════════════════════════════════════════
    if (focusMode) {
        const item = focusQueue[focusIdx];

        return (
            <div className={styles.focusShell}>
                {/* Left — queue list */}
                <div className={styles.focusLeft}>
                    <div className={styles.focusQueueLabel}>Queue · {focusQueue.length}</div>
                    {focusQueue.map((f, i) => (
                        <div
                            key={f.id}
                            className={`${styles.focusQueueItem} ${i === focusIdx ? styles.focusQueueItemActive : ''}`}
                            onClick={() => setFocusIdx(i)}
                        >
                            {f.status === 'done'
                                ? <Check size={10} className={styles.focusQueueDone} />
                                : <span className={styles.focusQueueNum}>{i + 1}</span>
                            }
                            <span className={styles.focusQueueName}>{f.name}</span>
                            <ScorePill score={f.score} />
                        </div>
                    ))}
                </div>

                {/* Right — active card */}
                <div className={styles.focusRight}>
                    <div className={styles.focusTopBar}>
                        <div className={styles.focusModeLabel}>
                            <Zap size={13} color="#d97706" />
                            <span className={styles.focusModeLabelText}>Focus Mode</span>
                            <span className={styles.focusModeSubtext}>{focusIdx + 1} of {focusQueue.length}</span>
                        </div>
                        <div className={styles.focusTopRight}>
                            <div className={styles.focusProgressBar} style={{ width: 120 }}>
                                <div className={styles.focusProgressFill} style={{ width: `${Math.round((focusIdx / focusQueue.length) * 100)}%` }} />
                            </div>
                            <button className={styles.focusExitBtn} onClick={() => { setFocusMode(false); setFocusIdx(0); }}>
                                <X size={11} /> Exit Focus
                            </button>
                        </div>
                    </div>

                    <div className={styles.focusProgressBar}>
                        <div className={styles.focusProgressFill} style={{ width: `${Math.round((focusIdx / focusQueue.length) * 100)}%` }} />
                    </div>

                    <div className={styles.focusBody}>
                        {!item ? (
                            <div className={styles.focusClearedWrap}>
                                <CheckCircle2 size={40} color="#16a34a" />
                                <div className={styles.focusClearedTitle}>Queue cleared!</div>
                                <div className={styles.focusClearedSub}>All urgent follow-ups handled for today.</div>
                                <button className={styles.focusBackBtn} onClick={() => { setFocusMode(false); setFocusIdx(0); }}>
                                    ← Back to list
                                </button>
                            </div>
                        ) : (() => {
                            const tier = getTier(item);
                            const tm = TIER_META[tier];
                            return (
                                <div className={`${styles.focusCard} ${styles[tm.focusBorder]}`}>
                                    <div className={styles.focusCardTop}>
                                        <div>
                                            <div className={styles.focusCandidateName}>{item.name}</div>
                                            <div className={styles.focusCandidateMeta}>
                                                <span className={styles.focusCandidateMetaText}>{item.role}</span>
                                                <span className={styles.dot}>·</span>
                                                <span className={styles.focusCandidateMetaText}>{item.city}</span>
                                                <span className={styles.dot}>·</span>
                                                <span className={styles.focusCandidateMetaText}>{item.salon}</span>
                                            </div>
                                        </div>
                                        <div className={styles.focusCardRight}>
                                            <ScorePill score={item.score} />
                                            <span className={`${styles.focusTimeLabel} ${styles[tm.focusTime]}`}>{timeLabel(item.scheduledAt)}</span>
                                        </div>
                                    </div>

                                    <div className={styles.focusNote}>
                                        <div className={styles.focusNoteText}>{item.note}</div>
                                    </div>

                                    <div className={styles.focusCardMeta}>
                                        <SrcBadge s={item.source} />
                                        <span className={styles.focusRecruiter}>{item.phone || 'No contact number'}</span>
                                        <span className={styles.focusRecruiter}>Snoozes: {Number(item.snooze_count || 0)}</span>
                                        <span className={styles.focusRecruiter}>Created: {formatMetaDateTime(item.created_at)}</span>
                                        {item.tags.includes('hot') && <span className={`${styles.tagPill} ${styles.tagHot}`}>HOT</span>}
                                        {item.tags.includes('employer_urgent') && <span className={`${styles.tagPill} ${styles.tagUrgent}`}>URGENT</span>}
                                        {item.tags.includes('competing_offer') && <span className={`${styles.tagPill} ${styles.tagCompeting}`}>COMPETING OFFER</span>}
                                        {item.tags.includes('salary_concern') && <span className={`${styles.tagPill} ${styles.tagSalary}`}>SALARY</span>}
                                        <span className={styles.focusRecruiter}>via {item.recruiter}</span>
                                    </div>

                                    <div className={styles.focusActionsMain}>
                                        <div className={styles.focusPrimaryContact}>
                                            <Phone size={15} />
                                            <span>{item.phone || 'No contact number'}</span>
                                        </div>
                                        <button className={styles.focusBtnCall} onClick={() => markDone(item.id, 'call', true)}>
                                            <Phone size={15} /> Call Now
                                        </button>
                                    </div>
                                    <div className={styles.focusActionsSub}>
                                        <button className={`${styles.focusSmBtn} ${styles.focusSmDone}`} onClick={() => markDone(item.id, 'done', true)}><Check size={11} /> Done</button>
                                        <button className={`${styles.focusSmBtn} ${styles.focusSmSnooze}`} onClick={() => { setSnoozeError(''); setSnoozeId(item.id); }}><Clock size={11} /> Snooze</button>
                                        <button className={`${styles.focusSmBtn} ${styles.focusSmSkip}`} onClick={() => setFocusIdx(i => i + 1)}>Skip →</button>
                                        <button className={`${styles.focusSmBtn} ${styles.focusSmDetail}`} onClick={() => openDetail(item.id)}><Eye size={11} /> History</button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {snoozeId && <SnoozeSheet
                    onClose={() => { if (!isSnoozing) { setSnoozeId(null); setSnoozeError(''); } }}
                    onSnooze={async (h, reason) => {
                        const isSuccess = await doSnooze(snoozeId, h, reason);
                        if (isSuccess) {
                            setFocusIdx(i => Math.min(i, focusQueue.length - 2));
                        }
                    }}
                    isLoading={isSnoozing && snoozingFollowupId === snoozeId}
                    errorMessage={snoozeError}
                />}
                {outcomeSheetState && <OutcomeSheet
                    followup={data.find((itemData) => itemData.id === outcomeSheetState.id) || null}
                    trigger={outcomeSheetState.trigger}
                    isLoading={isUpdatingOutcome}
                    errorMessage={outcomeError}
                    onClose={() => {
                        if (isUpdatingOutcome) return;
                        setOutcomeSheetState(null);
                        setOutcomeError('');
                    }}
                    onSubmit={(payload) => updateFollowupOutcome({ ...payload, id: outcomeSheetState.id })}
                />}
                {cancelModalState && (
                    <CancelModal
                        followup={data.find((item) => item.id === cancelModalState.id) || null}
                        errorMessage={cancelError}
                        onClose={() => { setCancelModalState(null); setCancelError(''); }}
                        onSubmit={cancelFollowup}
                    />
                )}
                {detailDrawerId !== null && (
                    <FollowupDetailDrawer
                        followup={data.find((item) => item.id === detailDrawerId) || null}
                        detailData={detailData}
                        isLoading={isFetchingDetail}
                        error={detailError}
                        onClose={closeDetail}
                        onRetry={retryDetail}
                    />
                )}
                {toast && <Toast message={toast.message} type={toast.type} />}
            </div>
        );
    }

    // ══════════════════════════════════════════════════════════
    //  MAIN TWO-PANEL LAYOUT
    // ══════════════════════════════════════════════════════════
    return (
        <div className={styles.shell}>
            {/* ══ MODALS ═══════════════════════════════════════════ */}
            {isAddNewFollowupOpen && <AddNewFollowup isOpen={isAddNewFollowupOpen} 
                onClose={() => setIsAddNewFollowupOpen(false)} 
                onAdd={addFollowup} 
                newNote={newNote} 
                setNewNote={setNewNote} 
                />
            } 
            {/* ══ LEFT PANEL ══════════════════════════════════════ */}
            <div className={styles.left}>

                {/* Brand */}
                <div className={styles.brand}>
                    <div className={styles.brandDot} />
                    <span className={styles.brandText}>Follow-ups</span>
                    {counts.overdue > 0 && (
                        <span style={{ background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 99, padding: '1px 6px', marginLeft: 'auto' }}>
                            {counts.overdue} overdue
                        </span>
                    )}
                </div>

                <div className={styles.sectionLabel}>Recruiters</div>
                <div className={styles.executiveSelectWrap}>
                    <Select
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        classNamePrefix="followup-executive-select"
                        options={recruiterSelectOptions}
                        value={selectedRecruiters}
                        onChange={(value) => {
                            const selectedValues = Array.isArray(value) ? value : [];
                            const hasAllToggle = selectedValues.some((item) => item?.value === ALL_RECRUITERS_OPTION_VALUE);

                            if (hasAllToggle) {
                                const areAllSelected = selectedRecruiters.length === recruiterOptions.length;
                                setSelectedRecruiters(areAllSelected ? [] : recruiterOptions);
                                return;
                            }

                            setSelectedRecruiters(
                                selectedValues.filter((item) => item?.value !== ALL_RECRUITERS_OPTION_VALUE)
                            );
                        }}
                        placeholder={selectedRecruiters.length === 0 ? `${getExecutiveFullName(authCtx?.userIdentifier) || authCtx?.userIdentifier} (default)` : 'Select additional recruiters'}
                        noOptionsMessage={() => 'No recruiters found'}
                        styles={EXECUTIVE_SELECT_STYLES}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                    <div className={styles.executiveSelectHint}>
                        {selectedRecruiters.length === 0 
                            ? 'Showing your follow-ups by default'
                            : `Showing ${selectedRecruiters.length} recruiter${selectedRecruiters.length > 1 ? 's' : ''}`}
                    </div>
                </div>

                {/* KPI grid */}
                <div className={styles.sectionLabel}>Overview</div>
                <div className={styles.kpiGrid}>
                    {[
                        { key: 'overdue', label: 'Overdue' },
                        { key: 'today', label: 'Today' },
                        { key: 'tomorrow', label: 'Tomorrow' },
                        { key: 'upcoming', label: 'Upcoming' },
                        { key: 'done', label: 'Done' },
                    ].map(s => (
                        <div
                            key={s.key}
                            className={`${styles.kpiCard} ${styles[STAT_CLS[s.key]]}`}
                            onClick={() => setTab(s.key)}
                        >
                            <div className={styles.kpiNum}>{counts[s.key]}</div>
                            <div className={styles.kpiLabel}>{s.label}</div>
                        </div>
                    ))}
                    <div className={`${styles.kpiCard} ${styles.kpiRate}`}>
                        <div className={styles.kpiNum}>{completionRate}%</div>
                        <div className={styles.kpiLabel}>Completion</div>
                    </div>
                </div>

                {/* Completion bar */}
                <div className={styles.completionWrap}>
                    <div className={styles.completionHeader}>
                        <span className={styles.completionTitle}>Today's progress</span>
                        <span className={styles.completionPct}>{completionRate}%</span>
                    </div>
                    <div className={styles.completionTrack}>
                        <div className={styles.completionFill} style={{ width: `${completionRate}%` }} />
                    </div>
                </div>

                <div className={styles.divider} />

                {/* Source breakdown */}
                <div className={styles.sectionLabel}>By Source</div>
                <div className={styles.sourceList}>
                    <div
                        className={`${styles.sourceRow} ${fSrc === 'all' ? styles.sourceRowActive : ''}`}
                        onClick={() => setFSrc('all')}
                    >
                        <div className={`${styles.sourceDot} ${styles.sourceDotManual}`} style={{ background: '#0369a1' }} />
                        <span className={styles.sourceName}>All sources</span>
                        <span className={styles.sourceCount}>{counts.all}</span>
                    </div>
                    {Object.entries(SRC_META).map(([key, meta]) => {
                        const c = srcCounts[key] || 0;
                        if (!c) return null;
                        return (
                            <div
                                key={key}
                                className={`${styles.sourceRow} ${fSrc === key ? styles.sourceRowActive : ''}`}
                                onClick={() => setFSrc(fSrc === key ? 'all' : key)}
                            >
                                <div className={`${styles.sourceDot} ${styles[meta.dot]}`} />
                                <span className={styles.sourceName}>{meta.label}</span>
                                <span className={styles.sourceCount}>{c}</span>
                            </div>
                        );
                    })}
                </div>

                <div className={styles.divider} />

                {/* Priority breakdown */}
                <div className={styles.sectionLabel}>By Priority</div>
                <div className={styles.priorityList}>
                    {[
                        { key: 'high', label: 'High', fill: 'priorityFillHigh' },
                        { key: 'medium', label: 'Medium', fill: 'priorityFillMedium' },
                        { key: 'low', label: 'Low', fill: 'priorityFillLow' },
                    ].map(p => (
                        <div
                            key={p.key}
                            className={`${styles.priorityRow} ${fPri === p.key ? styles.priorityRowActive : ''}`}
                            onClick={() => setFPri(fPri === p.key ? 'all' : p.key)}
                        >
                            <span className={styles.priorityName}>{p.label}</span>
                            <div className={styles.priorityBar}>
                                <div className={`${styles.priorityFill} ${styles[p.fill]}`} style={{ width: `${Math.round((priCounts[p.key] / priTotal) * 100)}%` }} />
                            </div>
                            <span className={styles.priorityCount}>{priCounts[p.key]}</span>
                        </div>
                    ))}
                </div>
                <div className={styles.divider} />

                {/* Focus mode CTA */}
                <button className={styles.focusCta} onClick={() => { setFocusMode(true); setFocusIdx(0); }}>
                    <Zap size={16} color="#fff" />
                    <div className={styles.focusCtaText}>
                        <span className={styles.focusCtaTitle}>Focus Mode</span>
                        <span className={styles.focusCtaSub}>{focusQueue.length} urgent to handle</span>
                    </div>
                    <ChevronRight size={14} className={styles.focusCtaArrow} />
                </button>

            </div>

            {/* ══ RIGHT PANEL ═════════════════════════════════════ */}
            <div className={styles.right}>

                {/* Top bar */}
                <div className={styles.topBar}>
                    <Bell size={14} color="#94a3b8" />
                    <div className={styles.searchWrap}>
                        <Search size={11} color="#94a3b8" />
                        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, role, city, salon, note…" />
                        {q && <button className={styles.searchClear} onClick={() => setQ('')}><X size={11} /></button>}
                    </div>
                    {isFetchingData && <span className={styles.fetchingText}>{isInitialDataLoading ? 'Loading follow-ups…' : 'Refreshing…'}</span>}
                    <button className={styles.addBtn} onClick={() => setIsAddNewFollowupOpen(true)}>
                        <Plus size={11} /> New follow-up
                    </button>
                </div>

                {/* Tabs */}
                <div className={styles.tabBar}>
                    {[
                        { key: 'all', label: 'All', alert: false },
                        { key: 'overdue', label: 'Overdue', alert: true },
                        { key: 'today', label: 'Today', alert: false },
                        { key: 'tomorrow', label: 'Tomorrow', alert: false },
                        { key: 'upcoming', label: 'Upcoming', alert: false },
                        { key: 'done', label: 'Done', alert: false },
                    ].map(item => (
                        <button
                            key={item.key}
                            className={`${styles.tab} ${tab === item.key ? styles.tabActive : ''}`}
                            onClick={() => setTab(item.key)}
                        >
                            {item.label}
                            {counts[item.key] > 0 && (
                                <span className={`${styles.tabBadge} ${item.alert && counts[item.key] > 0 ? styles.tabBadgeAlert : tab === item.key ? styles.tabBadgeActive : ''}`}>
                                    {counts[item.key]}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Active filter strip */}
                {activeFilterChips.length > 0 && (
                    <div className={styles.activeFilterStrip}>
                        <span className={styles.activeFilterLabel}>Filtered:</span>
                        {activeFilterChips.map(chip => (
                            <button key={chip.key} className={styles.activeFilterChip} onClick={chip.clear}>
                                {chip.label} <X size={9} />
                            </button>
                        ))}
                    </div>
                )}

                {/* Bulk bar */}
                {selected.size > 0 && (
                    <div className={styles.bulkBar}>
                        <span className={styles.bulkCount}>{selected.size} selected</span>
                        <div className={styles.bulkActions}>
                            <button className={styles.bulkDoneBtn} onClick={bulkDone}><Check size={10} /> Mark Done</button>
                            <button className={styles.bulkClearBtn} onClick={() => setSelected(new Set())}><X size={10} /> Clear</button>
                        </div>
                    </div>
                )}

                {/* List */}
                <div className={styles.listScroll}>
                    {isInitialDataLoading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.loadingSpinner} />
                            <div className={styles.loadingTitle}>Loading follow-ups…</div>
                            <div className={styles.loadingSubtext}>Fetching today&apos;s queue from backend</div>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className={styles.emptyState}>
                            <Inbox size={28} style={{ opacity: 0.3 }} />
                            <div className={styles.emptyText}>No follow-ups match these filters</div>
                        </div>
                    ) : grouped ? (
                        Object.entries(grouped).map(([tier, items]) => {
                            if (!items.length) return null;
                            const tm = TIER_META[tier];
                            return (
                                <div key={tier} className={styles.tierGroup}>
                                    <div className={styles.tierHeading}>
                                        <div className={`${styles.tierDot} ${styles[tm.dot]}`} />
                                        <span className={`${styles.tierLabel} ${styles[tm.labelCls]}`}>{tm.label}</span>
                                        <span className={styles.tierCount}>{items.length}</span>
                                        {tier === 'overdue' && <span className={styles.tierAlert}>Needs immediate attention</span>}
                                    </div>
                                    <div className={styles.tierCards}>
                                        {items.map(f => (
                                            <Card key={f.id} f={f} tier={tier} sel={selected.has(f.id)} onSel={() => toggleSel(f.id)} onDone={(trigger) => markDone(f.id, trigger, false)} onSnooze={() => { setSnoozeError(''); setSnoozeId(f.id); }} onCancel={() => { setCancelError(''); setCancelModalState({ id: f.id }); }} onDetail={() => openDetail(f.id)} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className={styles.tierCards}>
                            {filtered.map(f => (
                                <Card key={f.id} f={f} tier={getTier(f)} sel={selected.has(f.id)} onSel={() => toggleSel(f.id)} onDone={(trigger) => markDone(f.id, trigger, false)} onSnooze={() => { setSnoozeError(''); setSnoozeId(f.id); }} onCancel={() => { setCancelError(''); setCancelModalState({ id: f.id }); }} onDetail={() => openDetail(f.id)} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* MODALS */}
            {snoozeId && <SnoozeSheet
                onClose={() => { if (!isSnoozing) { setSnoozeId(null); setSnoozeError(''); } }}
                onSnooze={(h, reason) => doSnooze(snoozeId, h, reason)}
                isLoading={isSnoozing && snoozingFollowupId === snoozeId}
                errorMessage={snoozeError}
            />}
            {outcomeSheetState && <OutcomeSheet
                followup={data.find((itemData) => itemData.id === outcomeSheetState.id) || null}
                trigger={outcomeSheetState.trigger}
                isLoading={isUpdatingOutcome}
                errorMessage={outcomeError}
                onClose={() => {
                    if (isUpdatingOutcome) return;
                    setOutcomeSheetState(null);
                    setOutcomeError('');
                }}
                onSubmit={(payload) => updateFollowupOutcome({ ...payload, id: outcomeSheetState.id })}
            />}
            {addOpen && <AddSheet onClose={() => setAddOpen(false)} newNote={newNote} setNewNote={setNewNote} onAdd={addFollowup} />}
            {cancelModalState && (
                <CancelModal
                    followup={data.find((item) => item.id === cancelModalState.id) || null}
                    errorMessage={cancelError}
                    onClose={() => { setCancelModalState(null); setCancelError(''); }}
                    onSubmit={cancelFollowup}
                />
            )}
            {detailDrawerId !== null && (
                <FollowupDetailDrawer
                    followup={data.find((item) => item.id === detailDrawerId) || null}
                    detailData={detailData}
                    isLoading={isFetchingDetail}
                    error={detailError}
                    onClose={closeDetail}
                    onRetry={retryDetail}
                />
            )}
            {toast && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
}

// ─── CARD ────────────────────────────────────────────────────────────────────
function Card({ f, tier, sel, onSel, onDone, onSnooze, onCancel, onDetail }) {
    const [exp, setExp] = useState(false);
    const tm = TIER_META[tier] || TIER_META.upcoming;
    const done = f.status === 'done';
    const cancelled = f.status === 'cancelled';
    const pri = PRI_DOT[f.priority] || 'priLow';

    return (
        <div className={`${styles.card} ${styles[tm.cardCls]} ${done ? styles.isDone : ''} ${cancelled ? styles.isCancelled : ''}`}>
            <div className={styles.cardMain}>
                {!done && !cancelled && (
                    <div className={`${styles.checkbox} ${sel ? styles.checked : ''}`} onClick={e => { e.stopPropagation(); onSel(); }}>
                        {sel && <Check size={8} color="#fff" strokeWidth={3.5} />}
                    </div>
                )}
                <div className={`${styles.priorityDot} ${styles[pri]}`} />

                <div className={styles.candidateInfo} onClick={() => setExp(v => !v)}>
                    <div className={styles.candidateNameRow}>
                        <span className={styles.candidateName}>{f.name}</span>
                        <span className={styles.dot}>·</span>
                        <span className={styles.candidateRole}>{f.role}</span>
                        <span className={styles.dot}>·</span>
                        <span className={styles.candidateCity}>{f.city}</span>
                    </div>
                </div>

                <ScorePill score={f.score} />
                <span className={`${styles.timeLabel} ${styles[tm.timeCls]}`}>{timeLabel(f.scheduledAt)}</span>

                <div className={styles.cardActions} onClick={e => e.stopPropagation()}>
                    {!done && !cancelled && (
                        <>
                            <button title="Call" className={`${styles.iBtn} ${styles.iBtnCall}`} onClick={() => onDone('call')}><Phone size={10} /></button>
                            <button title="Done" className={`${styles.iBtn} ${styles.iBtnDone}`} onClick={() => onDone('done')}><Check size={10} /></button>
                            <button title="Snooze" className={`${styles.iBtn} ${styles.iBtnSnooze}`} onClick={onSnooze}><Clock size={10} /></button>
                            <button title="Cancel follow-up" className={`${styles.iBtn} ${styles.iBtnCancel}`} onClick={onCancel}><XCircle size={10} /></button>
                        </>
                    )}
                    <button title="View full history" className={`${styles.iBtn} ${styles.iBtnDetail}`} onClick={onDetail}><Eye size={10} /></button>
                </div>
                {done && <span className={styles.doneBadge}>done</span>}
                {cancelled && <span className={styles.cancelledBadge}>cancelled</span>}
            </div>

            <div className={`${styles.cardMeta} ${(done || cancelled) ? styles.metaIndentDone : styles.metaIndent}`}>
                <SrcBadge s={f.source} />
                <span className={styles.salonName}>{f.phone || 'No contact number'}</span>
                <span className={styles.salonName}>{f.salon}</span>
                <span className={styles.salonName}>Snoozes: {Number(f.snooze_count || 0)}</span>
                <span className={styles.salonName}>Created: {formatMetaDateTime(f.created_at)}</span>
                {f.tags.includes('hot') && <span className={`${styles.tagPill} ${styles.tagHot}`}>HOT</span>}
                {f.tags.includes('employer_urgent') && <span className={`${styles.tagPill} ${styles.tagUrgent}`}>URGENT</span>}
                {f.tags.includes('competing_offer') && <span className={`${styles.tagPill} ${styles.tagCompeting}`}>COMPETING OFFER</span>}
                {f.tags.includes('salary_concern') && <span className={`${styles.tagPill} ${styles.tagSalary}`}>SALARY</span>}
                {f.tags.includes('certified') && <span className={`${styles.tagPill} ${styles.tagCertified}`}>CERTIFIED</span>}
                {f.tags.includes('placed') && <span className={`${styles.tagPill} ${styles.tagPlaced}`}>PLACED</span>}
                {f.tags.includes('referral') && <span className={`${styles.tagPill} ${styles.tagReferral}`}>REFERRAL</span>}
            </div>

            {exp && (
                <div className={`${styles.expandedPanel} ${(done || cancelled) ? styles.metaIndentDone : styles.metaIndent}`}>
                    <div className={styles.expandedNote}>{f.note}</div>
                    <div className={styles.expandedMeta}>
                        <span className={styles.expandedMetaItem}>via {SRC_META[f.source]?.label || f.source}</span>
                        <span className={styles.expandedMetaItem}>·</span>
                        <span className={styles.expandedMetaItem}>{f.phone || 'No contact number'}</span>
                        <span className={styles.expandedMetaItem}>·</span>
                        <span className={styles.expandedMetaItem}>Snoozes: {Number(f.snooze_count || 0)}</span>
                        <span className={styles.expandedMetaItem}>·</span>
                        <span className={styles.expandedMetaItem}>Created: {formatMetaDateTime(f.created_at)}</span>
                        <span className={styles.expandedMetaItem}>·</span>
                        <span className={styles.expandedMetaItem}>{f.sourceLabel}</span>
                        <span className={styles.expandedMetaItem}>·</span>
                        <span className={styles.expandedMetaItem}>by {getExecutiveFullName(f.recruiterIdentifier)}</span>
                        <span className={styles.expandedMetaItem}>·</span>
                        <span className={styles.expandedMetaItem}>{f.type === 'call' ? 'Call' : 'WhatsApp'} follow-up</span>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── SNOOZE SHEET ────────────────────────────────────────────────────────────
function SnoozeSheet({ onClose, onSnooze, isLoading = false, errorMessage = '' }) {
    const [reason, setReason] = useState('');
    const n = new Date();
    const opts = [
        { label: '30 min', h: 0.5 },
        { label: '1 hour', h: 1 },
        { label: '3 hours', h: 3 },
        { label: 'Tonight', h: Math.max(0.5, 20 - n.getHours()) },
        { label: 'Tomorrow', h: Math.max(1, 24 - n.getHours() + 9) },
        { label: '2 days', h: 48 },
    ];
    return (
        <div className={styles.overlay}>
            <div className={styles.sheet}>
                <div className={styles.sheetHeader}>
                    <span className={styles.sheetTitle}>Snooze for</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isLoading}><X size={14} /></button>
                </div>
                <div className={styles.formField} style={{ marginBottom: 10 }}>
                    <label className={styles.formLabel}>Reason (optional)</label>
                    <input
                        className={styles.formInput}
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="Why are you snoozing this follow-up?"
                        disabled={isLoading}
                    />
                </div>
                <div className={styles.snoozeGrid}>
                    {opts.map(o => (
                        <button
                            key={o.label}
                            className={styles.snoozeBtn}
                            onClick={() => onSnooze(o.h, reason)}
                            disabled={isLoading}
                        >
                            <Clock size={13} color="#94a3b8" />
                            {isLoading ? 'Snoozing…' : o.label}
                        </button>
                    ))}
                </div>
                {!!errorMessage && (
                    <div style={{ marginTop: 10, fontSize: 11, color: '#dc2626' }}>
                        {errorMessage}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── OUTCOME SHEET ───────────────────────────────────────────────────────────
function OutcomeSheet({ onClose, onSubmit, followup, trigger, isLoading = false, errorMessage = '' }) {
    const [action, setAction] = useState('had_conversation');
    const [subAction, setSubAction] = useState('snooze');
    const [noConversationReason, setNoConversationReason] = useState(NO_CONVERSATION_REASON_OPTIONS[0]?.value || 'did_not_pick');
    const [snoozeHours, setSnoozeHours] = useState('1');
    const [snoozeReason, setSnoozeReason] = useState('');
    const [interviewDate, setInterviewDate] = useState('');
    const [outcomeNote, setOutcomeNote] = useState('');
    const [localError, setLocalError] = useState('');

    const triggerLabel =
        trigger === 'call' ? 'Call' : 'Done';

    const submitHandler = async () => {
        setLocalError('');

        if (action === 'had_conversation' && subAction === 'interview_scheduled' && !interviewDate) {
            setLocalError('Interview date is required.');
            return;
        }

        const isSuccess = await onSubmit({
            action,
            subAction,
            noConversationReason,
            snoozeHours,
            snoozeReason,
            interviewDate,
            outcomeNote,
        });

        if (!isSuccess) {
            return;
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.sheet}>
                <div className={styles.sheetHeader}>
                    <span className={styles.sheetTitle}>Update Follow-up Outcome</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isLoading}><X size={14} /></button>
                </div>

                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                    Triggered from: <strong>{triggerLabel}</strong> · Candidate: <strong>{followup?.name || '—'}</strong>
                </div>

                <div className={styles.formGroup}>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Conversation result</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <button
                                type="button"
                                onClick={() => setAction('had_conversation')}
                                disabled={isLoading}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 6,
                                    borderRadius: 10,
                                    border: action === 'had_conversation' ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                                    background: action === 'had_conversation' ? '#e0f2fe' : '#ffffff',
                                    color: '#0f172a',
                                    padding: '10px 8px',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    fontSize: 11,
                                    fontWeight: 700,
                                }}
                            >
                                <CheckCircle2 size={14} />
                                Had conversation
                            </button>
                            <button
                                type="button"
                                onClick={() => setAction('no_conversation')}
                                disabled={isLoading}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 6,
                                    borderRadius: 10,
                                    border: action === 'no_conversation' ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                                    background: action === 'no_conversation' ? '#e0f2fe' : '#ffffff',
                                    color: '#0f172a',
                                    padding: '10px 8px',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    fontSize: 11,
                                    fontWeight: 700,
                                }}
                            >
                                <X size={14} />
                                No conversation
                            </button>
                        </div>
                    </div>

                    {action === 'had_conversation' && (
                        <>
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Next step</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    <button
                                        type="button"
                                        onClick={() => setSubAction('snooze')}
                                        disabled={isLoading}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 6,
                                            borderRadius: 10,
                                            border: subAction === 'snooze' ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                                            background: subAction === 'snooze' ? '#e0f2fe' : '#ffffff',
                                            color: '#0f172a',
                                            padding: '10px 8px',
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            fontSize: 11,
                                            fontWeight: 700,
                                        }}
                                    >
                                        <Clock size={14} />
                                        Snooze
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSubAction('interview_scheduled')}
                                        disabled={isLoading}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 6,
                                            borderRadius: 10,
                                            border: subAction === 'interview_scheduled' ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                                            background: subAction === 'interview_scheduled' ? '#e0f2fe' : '#ffffff',
                                            color: '#0f172a',
                                            padding: '10px 8px',
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            fontSize: 11,
                                            fontWeight: 700,
                                        }}
                                    >
                                        <Check size={14} />
                                        Interview Scheduled
                                    </button>
                                </div>
                            </div>

                            {subAction === 'snooze' && (
                                <>
                                    <div className={styles.formField}>
                                        <label className={styles.formLabel}>Snooze hours</label>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 6 }}>
                                            {[
                                                { value: '1', label: '1h' },
                                                { value: '2', label: '2h' },
                                                { value: '3', label: '3h' },
                                                { value: '8', label: '8h' },
                                                { value: '24', label: '24h' },
                                            ].map((item) => (
                                                <button
                                                    key={item.value}
                                                    type="button"
                                                    onClick={() => setSnoozeHours(item.value)}
                                                    disabled={isLoading}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 4,
                                                        borderRadius: 10,
                                                        border: snoozeHours === item.value ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                                                        background: snoozeHours === item.value ? '#e0f2fe' : '#ffffff',
                                                        color: '#0f172a',
                                                        padding: '9px 6px',
                                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    <Clock size={12} />
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.formField}>
                                        <label className={styles.formLabel}>Snooze reason</label>
                                        <input
                                            className={styles.formInput}
                                            value={snoozeReason}
                                            onChange={(event) => setSnoozeReason(event.target.value)}
                                            placeholder="She asked to call back after 6pm"
                                            disabled={isLoading}
                                        />
                                    </div>
                                </>
                            )}

                            {subAction === 'interview_scheduled' && (
                                <div className={styles.formField}>
                                    <label className={styles.formLabel}>Interview date & time</label>
                                    <input
                                        type="datetime-local"
                                        className={styles.formInput}
                                        value={interviewDate}
                                        onChange={(event) => setInterviewDate(event.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {action === 'no_conversation' && (
                        <div className={styles.formField}>
                            <label className={styles.formLabel}>Reason</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, maxHeight: 220, overflowY: 'auto', paddingRight: 2 }}>
                                {NO_CONVERSATION_REASON_OPTIONS.map((item) => {
                                    const isSelected = noConversationReason === item.value;
                                    return (
                                        <button
                                            key={item.value}
                                            type="button"
                                            onClick={() => setNoConversationReason(item.value)}
                                            disabled={isLoading}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                textAlign: 'left',
                                                borderRadius: 10,
                                                border: isSelected ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                                                background: isSelected ? '#e0f2fe' : '#ffffff',
                                                color: '#0f172a',
                                                padding: '9px 8px',
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                fontSize: 11,
                                                fontWeight: isSelected ? 700 : 600,
                                            }}
                                        >
                                            <Phone size={12} />
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>
                                System auto-reschedules with 1h, 2h, then exponential back-off.
                            </div>
                        </div>
                    )}

                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Outcome note</label>
                        <textarea
                            className={styles.formTextarea}
                            value={outcomeNote}
                            onChange={(event) => setOutcomeNote(event.target.value)}
                            placeholder="Add a quick note for this outcome"
                            disabled={isLoading}
                        />
                    </div>

                    {(localError || errorMessage) && (
                        <div style={{ fontSize: 11, color: '#dc2626' }}>
                            {localError || errorMessage}
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button className={styles.btnPrimary} onClick={submitHandler} disabled={isLoading}>
                        {isLoading ? 'Updating…' : 'Update Follow-up'}
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose} disabled={isLoading}>Cancel</button>
                </div>
            </div>
        </div>
    );
}

// ─── ADD SHEET ───────────────────────────────────────────────────────────────
function AddSheet({ onClose, newNote, setNewNote, onAdd }) {
    return (
        <div className={styles.overlay}>
            <div className={styles.sheet}>
                <div className={styles.sheetHeader}>
                    <span className={styles.sheetTitle}>New Follow-up</span>
                    <button className={styles.iconBtn} onClick={onClose}><X size={14} /></button>
                </div>
                <div className={styles.formGroup}>
                    {[
                        { label: 'Candidate Name *', key: 'name', ph: 'e.g. Priya Sharma', textarea: false },
                        { label: 'Role', key: 'role', ph: 'e.g. Senior Stylist', textarea: false },
                        { label: 'City', key: 'city', ph: 'e.g. Delhi', textarea: false },
                        { label: 'Salon', key: 'salon', ph: 'e.g. Naturals CP', textarea: false },
                        { label: 'Note', key: 'note', ph: 'What to follow up about?', textarea: true },
                    ].map(f => (
                        <div key={f.key} className={styles.formField}>
                            <label className={styles.formLabel}>{f.label}</label>
                            {f.textarea
                                ? <textarea className={styles.formTextarea} value={newNote[f.key]} onChange={e => setNewNote(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.ph} />
                                : <input className={styles.formInput} value={newNote[f.key]} onChange={e => setNewNote(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.ph} />
                            }
                        </div>
                    ))}
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Remind in</label>
                        <select className={styles.formSelect} value={newNote.hours} onChange={e => setNewNote(p => ({ ...p, hours: e.target.value }))}>
                            {[['1', '1 hour'], ['3', '3 hours'], ['8', '8 hours'], ['24', 'Tomorrow'], ['48', 'In 2 days'], ['72', 'In 3 days']].map(([v, l]) => (
                                <option key={v} value={v}>{l}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.formActions}>
                    <button className={styles.btnPrimary} onClick={onAdd}>Add Follow-up</button>
                    <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
                </div>
            </div>
        </div>
    );
}

// ─── SOURCE BADGE ─────────────────────────────────────────────────────────────
function SrcBadge({ s }) {
    const meta = SRC_META[s] || SRC_META.manual;
    return <span className={`${styles.srcBadge} ${styles[meta.badge]}`}>{meta.label}</span>;
}

// ─── CANCEL MODAL ────────────────────────────────────────────────────────────
function CancelModal({ onClose, onSubmit, followup, errorMessage = '' }) {
    const [cancelReason, setCancelReason] = useState('');
    const [cancelNote, setCancelNote] = useState('');
    const [showNote, setShowNote] = useState(false);
    const [localError, setLocalError] = useState('');

    const isOther = cancelReason === 'other';
    const canSubmit = cancelReason && !(isOther && !cancelNote.trim());

    const handleSubmit = () => {
        setLocalError('');
        if (!cancelReason) {
            setLocalError('Please select a cancel reason.');
            return;
        }
        if (isOther && !cancelNote.trim()) {
            setLocalError('A note is required when reason is "Other".');
            return;
        }
        onSubmit({ cancelReason, cancelNote: cancelNote.trim() });
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.sheet}>
                <div className={styles.sheetHeader}>
                    <span className={styles.sheetTitle}>Cancel Follow-up</span>
                    <button className={styles.iconBtn} onClick={onClose}><X size={14} /></button>
                </div>

                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
                    Candidate: <strong>{followup?.name || '—'}</strong>
                    {followup?.status === 'in_progress' && (
                        <div style={{ marginTop: 8, fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 8px' }}>
                            ⚠ This follow-up is currently in progress.
                        </div>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Cancel reason *</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {CANCEL_REASONS.map((reason) => (
                                <label
                                    key={reason.value}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '8px 10px',
                                        borderRadius: 8,
                                        border: cancelReason === reason.value ? '1px solid #f87171' : '1px solid #e2e8f0',
                                        background: cancelReason === reason.value ? '#fef2f2' : '#fff',
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: cancelReason === reason.value ? 700 : 500,
                                        color: '#0f172a',
                                        userSelect: 'none',
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="cancel_reason"
                                        value={reason.value}
                                        checked={cancelReason === reason.value}
                                        onChange={() => {
                                            setCancelReason(reason.value);
                                            if (reason.value !== 'other') setShowNote(false);
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    {reason.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    {cancelReason && (
                        isOther ? (
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>
                                    Note <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <textarea
                                    className={styles.formTextarea}
                                    value={cancelNote}
                                    onChange={(e) => setCancelNote(e.target.value)}
                                    placeholder="Required — explain why you're cancelling"
                                    autoFocus
                                />
                            </div>
                        ) : showNote ? (
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Note (optional)</label>
                                <textarea
                                    className={styles.formTextarea}
                                    value={cancelNote}
                                    onChange={(e) => setCancelNote(e.target.value)}
                                    placeholder="Add a note for context"
                                    autoFocus
                                />
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowNote(true)}
                                style={{ fontSize: 11, color: '#0369a1', background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer', textAlign: 'left' }}
                            >
                                + Add note
                            </button>
                        )
                    )}

                    {(localError || errorMessage) && (
                        <div style={{ fontSize: 11, color: '#dc2626' }}>{localError || errorMessage}</div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button
                        className={styles.btnCancelSubmit}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        Cancel Follow-up
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose}>Back</button>
                </div>
            </div>
        </div>
    );
}

// ─── TOAST ───────────────────────────────────────────────────────────────────
function Toast({ message, type }) {
    return (
        <div className={`${styles.toast} ${type === 'error' ? styles.toastError : styles.toastSuccess}`}>
            {message}
        </div>
    );
}

// ─── FOLLOW-UP DETAIL DRAWER ─────────────────────────────────────────────────
function FollowupDetailDrawer({ followup, detailData, isLoading, error, onClose, onRetry }) {
    const [activityOpen, setActivityOpen] = useState(false);

    const timeline = useMemo(() => {
        if (!detailData) return [];
        const attempts = (detailData.attempts || []).map((a) => ({ ...a, _type: 'attempt', _ts: a.actioned_at }));
        const snoozes = (detailData.snooze_log || []).map((s) => ({ ...s, _type: 'snooze', _ts: s.snoozed_at }));
        return [...attempts, ...snoozes].sort((a, b) => new Date(a._ts) - new Date(b._ts));
    }, [detailData]);

    const fd = detailData?.followup;
    let attemptCounter = 0;

    return (
        <>
            <div className={styles.detailBackdrop} onClick={onClose} />
            <div className={styles.detailDrawer}>
                <div className={styles.detailHeader}>
                    <div className={styles.detailHeaderMain}>
                        <div className={styles.detailName}>{followup?.name || '—'}</div>
                        <div className={styles.detailMeta}>
                            <span>{followup?.role || '—'}</span>
                            <span className={styles.dot}>·</span>
                            <span>{followup?.city || '—'}</span>
                            <span className={styles.dot}>·</span>
                            <span>{followup?.salon || '—'}</span>
                        </div>
                        <div className={styles.detailHeaderBadges}>
                            {fd?.priority && (
                                <span className={`${styles.detailBadge} ${styles['detailPri_' + fd.priority]}`}>
                                    {fd.priority}
                                </span>
                            )}
                            {(fd?.snooze_count > 0) && (
                                <span className={styles.detailBadgeNeutral}>
                                    {fd.snooze_count} snooze{fd.snooze_count > 1 ? 's' : ''}
                                </span>
                            )}
                            {(fd?.tags || []).map((tag) => (
                                <span key={tag} className={`${styles.tagPill} ${styles['tag' + tag.charAt(0).toUpperCase() + tag.slice(1).replace(/_([a-z])/g, (m, c) => c.toUpperCase())]}`}>
                                    {tag.replace(/_/g, ' ').toUpperCase()}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button className={styles.detailClose} onClick={onClose}>
                        <X size={16} />
                    </button>
                </div>

                <div className={styles.detailScroll}>
                    {isLoading && <DetailSkeleton />}

                    {!isLoading && error && (
                        <div className={styles.detailErrorBox}>
                            <span className={styles.detailErrorText}>{error}</span>
                            <button className={styles.detailRetryBtn} onClick={onRetry}>Retry</button>
                        </div>
                    )}

                    {!isLoading && !error && detailData && (
                        <>
                            <div className={styles.detailSection}>
                                <div className={styles.detailSectionTitle}>Stats</div>
                                <div className={styles.statsGrid}>
                                    <StatTile label="Attempts" value={detailData.stats?.total_attempts ?? '—'} />
                                    <StatTile label="Connected" value={detailData.stats?.connected_attempts ?? '—'} accent />
                                    <StatTile label="No answer" value={detailData.stats?.no_answer_attempts ?? '—'} />
                                    <StatTile label="Snoozes" value={detailData.stats?.total_snoozes ?? '—'} />
                                    <StatTile
                                        label="Call time"
                                        value={detailData.stats?.total_call_minutes != null
                                            ? `${detailData.stats.total_call_minutes}m`
                                            : '—'}
                                    />
                                </div>
                            </div>

                            {timeline.length > 0 && (
                                <div className={styles.detailSection}>
                                    <div className={styles.detailSectionTitle}>Attempt Timeline</div>
                                    <div className={styles.timeline}>
                                        {timeline.map((item, idx) => {
                                            const isLast = idx === timeline.length - 1;
                                            if (item._type === 'attempt') {
                                                attemptCounter++;
                                                return (
                                                    <AttemptEntry
                                                        key={item.outcome_id || idx}
                                                        entry={item}
                                                        num={attemptCounter}
                                                        isLast={isLast}
                                                    />
                                                );
                                            }
                                            return <SnoozeEntry key={item.snooze_id || idx} entry={item} isLast={isLast} />;
                                        })}
                                    </div>
                                </div>
                            )}

                            {(fd?.note || fd?.internal_note) && (
                                <div className={styles.detailSection}>
                                    <div className={styles.detailSectionTitle}>Notes</div>
                                    {fd.note && <p className={styles.detailNote}>{fd.note}</p>}
                                    {fd.internal_note && (
                                        <p className={`${styles.detailNote} ${styles.detailNoteInternal}`}>
                                            <strong>Internal:</strong> {fd.internal_note}
                                        </p>
                                    )}
                                </div>
                            )}

                            {detailData.reminder_log?.length > 0 && (
                                <div className={styles.detailSection}>
                                    <div className={styles.detailSectionTitle}>Reminders</div>
                                    <div className={styles.reminderList}>
                                        {detailData.reminder_log.map((r) => (
                                            <ReminderRow key={r.reminder_id} r={r} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {detailData.activity_log?.length > 0 && (
                                <div className={styles.detailSection}>
                                    <button className={styles.detailSectionToggle} onClick={() => setActivityOpen((v) => !v)}>
                                        <span className={styles.detailSectionTitle} style={{ marginBottom: 0 }}>Activity Log</span>
                                        <span className={styles.detailSectionCount}>{detailData.activity_log.length} events</span>
                                        <ChevronRight size={12} style={{ marginLeft: 'auto', transform: activityOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', color: '#94a3b8' }} />
                                    </button>
                                    {activityOpen && (
                                        <div className={styles.activityList}>
                                            {detailData.activity_log.map((a) => (
                                                <ActivityRow key={a.activity_id} a={a} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

// ─── STAT TILE ────────────────────────────────────────────────────────────────
function StatTile({ label, value, accent }) {
    return (
        <div className={`${styles.statTile} ${accent ? styles.statTileAccent : ''}`}>
            <div className={styles.statValue}>{value}</div>
            <div className={styles.statLabel}>{label}</div>
        </div>
    );
}

// ─── ATTEMPT ENTRY ────────────────────────────────────────────────────────────
function AttemptEntry({ entry, num, isLast }) {
    const meta = OUTCOME_META[entry.outcome_type] || { label: entry.outcome_type || '—', color: '#64748b', bg: '#f1f5f9' };
    const dur = entry.call_duration_seconds;
    const durLabel = dur != null ? (dur >= 60 ? `${Math.floor(dur / 60)}m ${dur % 60}s` : `${dur}s`) : null;
    const dt = entry.actioned_at ? new Date(entry.actioned_at) : null;
    const dtLabel = dt
        ? dt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })
        : '—';

    return (
        <div className={styles.timelineItem}>
            <div className={styles.timelineConnector}>
                <div className={styles.timelineDot} style={{ background: meta.color }} />
                {!isLast && <div className={styles.timelineLine} />}
            </div>
            <div className={styles.timelineContent}>
                <div className={styles.timelineTop}>
                    <span className={styles.timelineNum}>#{num}</span>
                    <span className={styles.outcomeBadge} style={{ background: meta.bg, color: meta.color }}>
                        {meta.label}
                    </span>
                    {durLabel && <span className={styles.timelineDur}>{durLabel}</span>}
                    <span className={styles.timelineTs}>{dtLabel}</span>
                </div>
                {entry.outcome_note && (
                    <div className={styles.timelineNote}>"{entry.outcome_note}"</div>
                )}
                {entry.salary_discussed != null && (
                    <div className={styles.timelineExtra}>Salary discussed: ₹{Number(entry.salary_discussed).toLocaleString('en-IN')}</div>
                )}
                {entry.joining_date_committed && (
                    <div className={styles.timelineExtra}>Joining committed: {entry.joining_date_committed}</div>
                )}
                {entry.actioned_by && (
                    <div className={styles.timelineBy}>by {getExecutiveFullName(entry.actioned_by) || entry.actioned_by}</div>
                )}
            </div>
        </div>
    );
}

// ─── SNOOZE ENTRY ─────────────────────────────────────────────────────────────
function SnoozeEntry({ entry, isLast }) {
    const trigger = SNOOZE_TRIGGER_LABEL[entry.snooze_trigger] || entry.snooze_trigger || 'Snoozed';
    const dt = entry.snoozed_at ? new Date(entry.snoozed_at) : null;
    const dtLabel = dt
        ? dt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })
        : '—';
    const mins = entry.snooze_duration_minutes || 0;
    const durLabel = mins >= 60 ? `${Math.round(mins / 60 * 10) / 10}h` : `${mins}m`;

    return (
        <div className={styles.timelineItem}>
            <div className={styles.timelineConnector}>
                <div className={styles.timelineDotSnooze} />
                {!isLast && <div className={styles.timelineLine} />}
            </div>
            <div className={styles.timelineContent}>
                <div className={styles.timelineTop}>
                    <span className={styles.snoozeBadge}>↷ Snoozed {durLabel} · {trigger}</span>
                    <span className={styles.timelineTs}>{dtLabel}</span>
                </div>
                {entry.snooze_reason && (
                    <div className={styles.timelineNote}>{entry.snooze_reason}</div>
                )}
            </div>
        </div>
    );
}

// ─── REMINDER ROW ─────────────────────────────────────────────────────────────
function ReminderRow({ r }) {
    const statusColor = { delivered: '#15803d', sent: '#0369a1', queued: '#d97706', failed: '#dc2626', suppressed: '#64748b' };
    const color = statusColor[r.status] || '#64748b';
    const dt = r.sent_at || r.queued_at;
    const dtLabel = dt
        ? new Date(dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })
        : '—';

    return (
        <div className={styles.reminderRow}>
            <span className={styles.reminderChannel}>{r.channel}</span>
            <span className={styles.reminderTrigger}>{r.trigger_type}</span>
            <span className={styles.reminderStatus} style={{ color }}>{r.status}</span>
            <span className={styles.reminderTs}>{dtLabel}</span>
            {r.failure_reason && <span className={styles.reminderFailure}>{r.failure_reason}</span>}
        </div>
    );
}

// ─── ACTIVITY ROW ─────────────────────────────────────────────────────────────
function ActivityRow({ a }) {
    const dt = a.activity_at ? new Date(a.activity_at) : null;
    const dtLabel = dt
        ? dt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })
        : '—';
    const typeLabel = (a.activity_type || '—').replace(/_/g, ' ');

    return (
        <div className={styles.activityRow}>
            <div className={styles.activityLeft}>
                <div className={styles.activityType}>{typeLabel}</div>
                {a.activity_note && <div className={styles.activityNote}>{a.activity_note}</div>}
            </div>
            <div className={styles.activityRight}>
                <div className={styles.activityBy}>{getExecutiveFullName(a.executive_identifier) || a.executive_identifier}</div>
                <div className={styles.activityTs}>{dtLabel}</div>
            </div>
        </div>
    );
}

// ─── DETAIL SKELETON ─────────────────────────────────────────────────────────
function DetailSkeleton() {
    return (
        <div className={styles.skeletonWrap}>
            <div className={styles.skeletonStatsRow}>
                {[1, 2, 3, 4, 5].map((i) => <div key={i} className={styles.skeletonStatTile} />)}
            </div>
            <div className={styles.skeletonSectionTitle} />
            {[1, 2, 3].map((i) => (
                <div key={i} className={styles.skeletonTimelineItem}>
                    <div className={styles.skeletonDot} />
                    <div className={styles.skeletonLines}>
                        <div className={styles.skeletonLine} style={{ width: '55%' }} />
                        <div className={styles.skeletonLine} style={{ width: '80%', height: 8 }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── SCORE PILL ──────────────────────────────────────────────────────────────
function ScorePill({ score }) {
    const tier = score >= 80 ? 'High' : score >= 60 ? 'Mid' : 'Low';
    return (
        <div className={styles.scorePill}>
            <div className={styles.scoreTrack}>
                <div className={`${styles.scoreFill} ${styles['scoreFill' + tier]}`} style={{ width: `${score}%` }} />
            </div>
            <span className={`${styles.scoreNum} ${styles['scoreNum' + tier]}`}>{score}</span>
        </div>
    );
}